import React, { useEffect, useState } from "react";
import TeamSpiderGraph from "./TeamSpiderGraph";
import StressCircle from "./StressCircle";
import { useNavigate } from "react-router-dom";
import useOrganization from "../../organization/hooks/useOrganization";
import { useTeam } from "../../TeamcompositeResults/contexts/TeamContext";
import {
  getTeamCompositeScores,
  getTeamMemberScores,
} from "../../TeamcompositeResults/api/team.api";
import type { TeamMemberScore } from "../../TeamcompositeResults/api/team.api";

const stressKeyMap = [
  { key: "Arguing", label: "Arguing", color: "#f4a261" },
  { key: "Freezing", label: "Freezing", color: "#6c757d" },
  { key: "Retreating", label: "Retreating", color: "#6c757d" },
  { key: "Audacious", label: "Audacious", color: "#ef4444" },
  { key: "Appeasing", label: "Appeasing", color: "#6c757d" },
];

const TeamResultsPreview: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const { selectedTeam } = useTeam();
  const navigate = useNavigate();

  const [stressData, setStressData] = useState<any[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMemberScore[]>([]);
  const [loading, setLoading] = useState(false);

  const questionnaireId = currentOrganization?.default_survey;
  const teamId = selectedTeam?.id;

  useEffect(() => {
    if (!questionnaireId || !teamId) return;

    const loadScores = async () => {
      setLoading(true);

      try {
        const [compositeRes, membersRes] = await Promise.all([
          getTeamCompositeScores({
            team_id: teamId,
            questionnaire_id: Number(questionnaireId),
          }),
          getTeamMemberScores({
            team_id: teamId,
            questionnaire_id: Number(questionnaireId),
            limit: 15,
          }),
        ]);

        if (compositeRes.success) {
          const scores = compositeRes.data?.scores || {};
          const stressMapped = stressKeyMap.map((item) => ({
            label: item.label,
            value: scores[item.key] ? Math.round(scores[item.key]) : 0,
            color: item.color,
          }));
          setStressData(stressMapped);
        }

        if (membersRes.success) {
          setTeamMembers(membersRes.data?.members ?? []);
        }
      } catch (err) {
        console.error("Error loading team scores:", err);
      } finally {
        setLoading(false);
      }
    };

    loadScores();
  }, [questionnaireId, teamId]);

  if (!selectedTeam) {
    return (
      <div className="bg-white rounded-xl p-5 text-center text-gray-400">
        Select a team to view results
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 h-full flex flex-col">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-[#26295e] font-semibold">
          {selectedTeam.name} Results
        </h4>

        <button
          className="
      bg-[#3b519c]
      hover:bg-[#2f407c]
      text-white
      px-4 py-2
      rounded-lg
      text-sm
      shadow-sm
      transition
    "
          onClick={() =>
            navigate("/individual-results", {
              state: {
                teamId: selectedTeam.id,
                teamName: selectedTeam.name,
              },
            })
          }
        >
          View Results
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-400">
          Loading team results...
        </div>
      ) : (
        <>
          {/* RAPID TRUST */}
          <h6 className="mb-3 text-[#26295e] text-sm">Rapid Trust Build</h6>

          <div className="bg-white rounded-xl p-4 mb-8">
            <TeamSpiderGraph apiMembers={teamMembers} />
          </div>

          {/* STRESS RESPONSE */}
          <h6 className="mb-3 text-[#26295e] text-sm">
            Unprofessional Stress Response
          </h6>

          <div className="flex flex-wrap gap-3 justify-between mb-6">
            {stressData.map((item, index) => (
              <StressCircle
                key={index}
                label={item.label}
                value={item.value}
                color={item.color}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default TeamResultsPreview;
